import { Edge, Node } from '@xyflow/react';

import { StepType } from '@frontend/types/workflow-agent';

import { generateNodeId } from '../services/workflow-id-utils';
import { isConditionNode, isSpecializedNode, isStepNode } from '../types';

/**
 * Check if the connection is from a specialized node to a condition node.
 * Uses type guards for proper type narrowing.
 */
export function isSpecializedToConditionConnection(sourceNode: Node, targetNode: Node): boolean {
  // Use type guards to narrow the types
  if (!isSpecializedNode(sourceNode)) return false;
  if (!isConditionNode(targetNode)) return false;
  return true;
}

/**
 * Evaluates if a node needs type replacement based on its condition connections
 * Handles bidirectional transformations:
 * - Specialized + condition connections → Conditional
 * - Conditional + no condition connections → Specialized
 * Returns replacement info if needed, null if no change required
 */
export function evaluateAndReplaceNodeType(
  targetNodeId: string,
  nodes: Node[],
  edges: Edge[]
): { shouldReplace: boolean; newNode?: Node; newNodeId?: string; updatedEdges?: Edge[] } {
  const targetNode = nodes.find(n => n.id === targetNodeId);
  if (!targetNode) {
    return { shouldReplace: false };
  }

  // Use type guard to check if this is a step node
  if (!isStepNode(targetNode)) {
    return { shouldReplace: false };
  }

  // After isStepNode check, targetNode.data is typed as StepNodeData
  const stepData = targetNode.data;
  const currentType = stepData.type;

  // Only handle specialized and conditional nodes
  if (currentType !== StepType.SPECIALIZED && currentType !== StepType.CONDITIONAL) {
    return { shouldReplace: false };
  }

  // Count condition connections from this node (this node -> condition nodes)
  const conditionConnections = edges.filter(edge => {
    const targetNode = nodes.find(n => n.id === edge.target);
    return edge.source === targetNodeId && targetNode && targetNode.type === 'condition';
  });

  const hasConditions = conditionConnections.length > 0;

  // Determine if transformation is needed
  const shouldTransform =
    (currentType === StepType.SPECIALIZED && hasConditions) || // Specialized → Conditional
    (currentType === StepType.CONDITIONAL && !hasConditions); // Conditional → Specialized

  if (!shouldTransform) {
    return { shouldReplace: false };
  }

  const newNodeId = generateNodeId();
  let newNodeData: Record<string, unknown>;
  let newNodeType: string;

  if (currentType === StepType.SPECIALIZED) {
    // Convert specialized to conditional
    const specializedData = stepData as { agent_id?: string; step_id: string };
    newNodeData = {
      ...stepData,
      type: StepType.CONDITIONAL,
      step_id: newNodeId,
      evaluator_agent_id: specializedData.agent_id || specializedData.step_id || targetNodeId,
      conditions: [], // Will be populated by graph-to-workflow logic
    };
    delete newNodeData.agent_id; // Remove specialized-specific field
    newNodeType = 'conditional';
  } else {
    // Convert conditional back to specialized
    const conditionalData = stepData as { evaluator_agent_id?: string; step_id: string };
    newNodeData = {
      ...stepData,
      type: StepType.SPECIALIZED,
      step_id: newNodeId,
      agent_id: conditionalData.evaluator_agent_id || conditionalData.step_id || targetNodeId,
    };
    delete newNodeData.evaluator_agent_id; // Remove conditional-specific field
    delete newNodeData.conditions; // Remove conditional-specific field
    newNodeType = 'specialized';
  }

  const newNode: Node = {
    ...targetNode,
    id: newNodeId,
    type: newNodeType,
    data: newNodeData,
  };

  // Update all edges that reference the old node ID
  const updatedEdges = edges.map(edge => {
    if (edge.source === targetNodeId) {
      return { ...edge, source: newNodeId };
    }
    if (edge.target === targetNodeId) {
      return { ...edge, target: newNodeId };
    }
    return edge;
  });

  return {
    shouldReplace: true,
    newNode,
    newNodeId,
    updatedEdges,
  };
}
